import React, { useState, useContext, useRef, useEffect } from "react";
import { useAuthQuery as useQuery } from '../../../shared/hooks/useAuthQuery';
import { useMutation } from "convex/react";
import { useNavigate } from "react-router-dom";
import Markdown from "react-markdown";
import { api } from "../../../convex/_generated/api";
import { BaseContext } from "../../../shared/base-context";
import { Id } from "../../../convex/_generated/dataModel";
import { Button } from "../../../catalyst-components/button";

interface AppGuideChatPanelProps {
  onScrollToSection?: (slug: string) => void;
}

export const AppGuideChatPanel: React.FC<AppGuideChatPanelProps> = ({ onScrollToSection }) => {
  const { convexUserId } = useContext(BaseContext);
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const conversation = useQuery(
    api.app_guide.getConversation,
    convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip"
  );
  const sendMessage = useMutation(api.app_guide.sendMessage);
  const clearConversation = useMutation(api.app_guide.clearConversation);

  const messages = conversation?.messages || [];
  // Detect "waiting for response" — last message is from user
  const isWaiting = messages.length > 0 && messages[messages.length - 1].role === "user";

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 96) + "px";
    }
  }, [input]);

  const handleSend = async () => {
    if (!input.trim() || !convexUserId || isWaiting) return;
    const msg = input.trim();
    setInput("");
    await sendMessage({
      user_id: convexUserId as Id<"users">,
      content: msg,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full border rounded-lg bg-white">
      {/* Header */}
      <div className="flex justify-between items-center p-3 border-b bg-gray-50 rounded-t-lg">
        <h3 className="font-semibold text-sm text-gray-700">Ask about the guide</h3>
        {messages.length > 0 && (
          <button
            className="text-xs text-gray-500 hover:text-red-500 transition-colors"
            onClick={() => clearConversation({ user_id: convexUserId as Id<"users"> })}
          >
            Clear chat
          </button>
        )}
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-12 px-4">
            <div className="text-3xl mb-3">📖</div>
            <p className="text-sm text-gray-500">
              Ask questions about how the app works. Answers are based on the guide content.
            </p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i}>
            <div
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[90%] px-3 py-2 rounded-lg text-sm ${
                  msg.role === "user"
                    ? "bg-primary-5 text-white"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {msg.role === "assistant" ? (
                  <Markdown
                    components={{
                      // Keep prose styling compact for chat
                      p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
                      ul: ({ children }) => <ul className="list-disc ml-4 mb-1">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal ml-4 mb-1">{children}</ol>,
                      code: ({ children }) => (
                        <code className="bg-gray-200 px-1 rounded text-xs">{children}</code>
                      ),
                      pre: ({ children }) => (
                        <pre className="bg-gray-200 p-2 rounded text-xs overflow-x-auto my-1">
                          {children}
                        </pre>
                      ),
                      h1: ({ children }) => <h3 className="font-bold mt-2 mb-1">{children}</h3>,
                      h2: ({ children }) => <h3 className="font-bold mt-2 mb-1">{children}</h3>,
                      h3: ({ children }) => <h4 className="font-semibold mt-1 mb-1">{children}</h4>,
                      a: ({ href, children }) => {
                        // Internal links: use router navigation
                        if (href?.startsWith("/")) {
                          return (
                            <button
                              onClick={(e) => { e.preventDefault(); navigate(href); }}
                              className="text-primary-6 underline hover:text-primary-7 cursor-pointer"
                            >
                              {children}
                            </button>
                          );
                        }
                        // External links: open in new tab
                        return (
                          <a href={href} target="_blank" rel="noopener noreferrer"
                            className="text-primary-6 underline hover:text-primary-7">
                            {children}
                          </a>
                        );
                      },
                    }}
                  >
                    {msg.content}
                  </Markdown>
                ) : (
                  <span className="whitespace-pre-wrap">{msg.content}</span>
                )}
              </div>
            </div>
            {/* Source reference chips */}
            {msg.role === "assistant" && msg.sources && msg.sources.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1 ml-1">
                <span className="text-[10px] text-gray-400 self-center mr-0.5">Sources:</span>
                {msg.sources.map((source, j) => (
                  <button
                    key={j}
                    onClick={() => onScrollToSection?.(source.slug)}
                    className="text-[10px] px-1.5 py-0.5 bg-gray-200 hover:bg-primary-5/20 text-gray-600 hover:text-primary-6 rounded transition-colors cursor-pointer"
                    title={`Scroll to: ${source.heading}`}
                  >
                    {source.heading}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        {isWaiting && (
          <div className="flex justify-start">
            <div className="bg-gray-100 text-gray-500 px-3 py-2 rounded-lg text-sm animate-pulse">
              Thinking...
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="p-3 border-t">
        <div className="flex gap-2 items-end">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question..."
            className="flex-1 text-sm border rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary-5/50 focus:border-primary-5"
            style={{ minHeight: "2.5rem", maxHeight: "6rem" }}
            disabled={isWaiting}
            rows={1}
          />
          <Button
            color="dark"
            onClick={handleSend}
            disabled={!input.trim() || isWaiting}
            className="shrink-0"
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};
